// DESCRIPTION: Month view — a 7-column calendar grid with colored event dots per day cell.
// Clicking a day reveals all events in a popover overlay. Multi-day events render as spanning banners.

import React, { useState, useEffect, useRef } from 'react';
import EventCell from './EventCell';
import MiniCalendar from './MiniCalendar';
import MobileAgenda from './MobileAgenda';
import {
	isMultiDayEvent,
	getEventSpanDates,
	formatTime,
	getDisplayTime,
} from './utils';

const config = window.nemtlyCalendar || {};
const strings = config.strings || {};

const SHORT_DAYS_SUN = [ 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat' ];
const SHORT_DAYS_MON = [ 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun' ];

/**
 * A palette of soft background colors for event dots. Cycles through these
 * for events that don't have a category color assigned.
 */
const DOT_PALETTE = [
	{ bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' }, // blue
	{ bg: '#fce7f3', text: '#9d174d', border: '#f9a8d4' }, // pink
	{ bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' }, // green
	{ bg: '#fef3c7', text: '#92400e', border: '#fcd34d' }, // amber
	{ bg: '#ede9fe', text: '#5b21b6', border: '#c4b5fd' }, // violet
	{ bg: '#ffedd5', text: '#9a3412', border: '#fdba74' }, // orange
];

/**
 * Convert a Date to YYYY-MM-DD.
 *
 * @param {Date} d
 *
 * @return {string}
 */
function toISO( d ) {
	return d.toISOString().slice( 0, 10 );
}

/**
 * Group events by their start date string (YYYY-MM-DD).
 *
 * @param {Array} events
 *
 * @return {Object} Map of dateString → events[]
 */
function groupByDate( events ) {
	const map = {};
	events.forEach( ( event ) => {
		const key = event.date_utc || event.date || '';
		if ( ! map[ key ] ) {
			map[ key ] = [];
		}
		map[ key ].push( event );
	} );
	return map;
}

/**
 * Resolve the dot color for an event — uses category color if available,
 * otherwise cycles through the palette based on event index.
 *
 * @param {Object} event Calendar event object.
 * @param {number} index Index of the event within the day (for palette cycling).
 *
 * @return {{ bg: string, text: string, border: string }}
 */
function getDotColor( event, index ) {
	const colorBy = config.colorBy || 'category';
	const categoryColors = config.categoryColors || {};

	if (
		colorBy === 'category' &&
		event.categories &&
		event.categories.length
	) {
		for ( const cat of event.categories ) {
			const id = typeof cat === 'object' ? cat.id : cat;
			if ( categoryColors[ id ] ) {
				// Derive lighter bg from the category color.
				return {
					bg: categoryColors[ id ] + '20',
					text: categoryColors[ id ],
					border: categoryColors[ id ] + '60',
				};
			}
		}
	}

	return DOT_PALETTE[ index % DOT_PALETTE.length ];
}

/**
 * Build the multi-day spanning layout for a single week row.
 *
 * Each span object describes which columns it occupies (1-based, for CSS grid-column).
 *
 * @param {object[]} multiDayEvents All multi-day events in this month.
 * @param {string[]} weekDates      Array of 7 ISO date strings for this row.
 *
 * @return {object[]} Array of { event, colStart, colEnd } where cols are 1-based.
 */
function buildSpansForWeek( multiDayEvents, weekDates ) {
	const spans = [];
	const weekStart = weekDates[ 0 ];
	const weekEnd = weekDates[ 6 ];

	multiDayEvents.forEach( ( event ) => {
		const eventDates = getEventSpanDates( event, weekStart, weekEnd );
		if ( eventDates.length === 0 ) {
			return;
		}

		// Find the column indices (0-based) for the first and last date in this week.
		const firstIdx = weekDates.indexOf( eventDates[ 0 ] );
		const lastIdx = weekDates.indexOf(
			eventDates[ eventDates.length - 1 ]
		);
		if ( firstIdx < 0 || lastIdx < 0 ) {
			return;
		}

		// Use local date for events with explicit end_date (both are local timezone).
		const eventStartDate =
			event.end_date && event.end_date !== ( event.date || '' )
				? event.date || event.date_utc
				: event.date_utc || event.date;
		const isStart = eventDates[ 0 ] === eventStartDate;
		const allEventDates = getEventSpanDates(
			event,
			'0000-01-01',
			'9999-12-31'
		);
		const isEnd =
			eventDates[ eventDates.length - 1 ] ===
			allEventDates[ allEventDates.length - 1 ];

		spans.push( {
			event,
			colStart: firstIdx + 1, // CSS grid-column is 1-based
			colEnd: lastIdx + 2, // grid-column end is exclusive
			isStart,
			isEnd,
		} );
	} );

	return spans;
}

/**
 * Hook that returns true when the viewport width is below the mobile breakpoint.
 *
 * @param {number} breakpoint Pixel width threshold.
 *
 * @return {boolean}
 */
function useIsMobile( breakpoint = 768 ) {
	const [ isMobile, setIsMobile ] = useState(
		typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
	);

	useEffect( () => {
		const mql = window.matchMedia( `(max-width: ${ breakpoint - 1 }px)` );
		const handler = ( e ) => setIsMobile( e.matches );
		mql.addEventListener( 'change', handler );
		setIsMobile( mql.matches );
		return () => mql.removeEventListener( 'change', handler );
	}, [ breakpoint ] );

	return isMobile;
}

/**
 * DayPopover — shows the full event list for a day when the user clicks a cell.
 *
 * @param {Object}   props
 * @param {string}   props.dateStr    ISO date string.
 * @param {Array}    props.events     Events for this day.
 * @param {boolean}  props.useLocalTz Whether to show times in browser's local timezone.
 * @param {Function} props.onClose    Close callback.
 * @param {Function} props.onDayClick Navigate to day view.
 */
function DayPopover( { dateStr, events, useLocalTz, onClose, onDayClick } ) {
	const ref = useRef( null );

	// Close when clicking outside.
	useEffect( () => {
		const handleClick = ( e ) => {
			if ( ref.current && ! ref.current.contains( e.target ) ) {
				onClose();
			}
		};
		// Use setTimeout to avoid the same click that opened it from closing it.
		const timer = setTimeout( () => {
			document.addEventListener( 'mousedown', handleClick );
		}, 0 );
		return () => {
			clearTimeout( timer );
			document.removeEventListener( 'mousedown', handleClick );
		};
	}, [ onClose ] );

	// Close on Escape.
	useEffect( () => {
		const handleKey = ( e ) => {
			if ( e.key === 'Escape' ) {
				onClose();
			}
		};
		document.addEventListener( 'keydown', handleKey );
		return () => document.removeEventListener( 'keydown', handleKey );
	}, [ onClose ] );

	const d = new Date( dateStr + 'T00:00:00' );
	const heading = d.toLocaleDateString( undefined, {
		weekday: 'short',
		month: 'long',
		day: 'numeric',
	} );

	return (
		<div
			ref={ ref }
			className="nb-calendar-day-popover"
			role="dialog"
			aria-modal="true"
		>
			<div className="nb-calendar-day-popover__header">
				<span className="nb-calendar-day-popover__title">
					{ heading }
				</span>
				<button
					className="nb-calendar-day-popover__close"
					onClick={ onClose }
					aria-label="Close"
				>
					&times;
				</button>
			</div>

			<div className="nb-calendar-day-popover__events">
				{ events.length === 0 && (
					<p className="nb-calendar-day-popover__empty">
						{ strings.noEvents || 'No events this day.' }
					</p>
				) }

				{ events.map( ( event ) => (
					<EventCell
						key={ event.id }
						event={ event }
						isAllDay={ ! event.time }
						useLocalTz={ useLocalTz }
					/>
				) ) }
			</div>

			<div className="nb-calendar-day-popover__footer">
				<button
					className="nb-calendar-day-popover__view-day"
					onClick={ () => {
						onClose();
						onDayClick( d );
					} }
				>
					{ strings.viewDay || 'View full day' }
				</button>
			</div>
		</div>
	);
}

export default function MonthView( {
	events,
	anchor,
	firstDay,
	eventsPerCell = 3,
	dateFrom,
	useLocalTz,
	onDayClick,
} ) {
	const [ activeDate, setActiveDate ] = useState( null );
	const [ mobileSelectedDate, setMobileSelectedDate ] = useState( null );
	const isMobile = useIsMobile();

	const dayLabels = firstDay === 1 ? SHORT_DAYS_MON : SHORT_DAYS_SUN;

	// Build 42 grid cells starting from dateFrom.
	const gridStart = new Date( dateFrom );
	const cells = Array.from( { length: 42 }, ( _, i ) => {
		const d = new Date( gridStart );
		d.setDate( d.getDate() + i );
		return d;
	} );

	const gridStartStr = toISO( cells[ 0 ] );
	const gridEndStr = toISO( cells[ 41 ] );

	// Separate multi-day from single-day events.
	const multiDayEvents = events.filter( ( e ) => isMultiDayEvent( e ) );
	const singleDayEvents = events.filter( ( e ) => ! isMultiDayEvent( e ) );
	const singleByDate = groupByDate( singleDayEvents );

	// Build week rows (6 rows of 7 days each).
	const weekRows = [];
	for ( let w = 0; w < 6; w++ ) {
		weekRows.push( cells.slice( w * 7, w * 7 + 7 ) );
	}

	const today = new Date();
	const todayStr = toISO( today );
	const monthYear = `${ anchor.getFullYear() }-${ String(
		anchor.getMonth() + 1
	).padStart( 2, '0' ) }`;

	// Build a set of dates that have events (for mini-calendar dot indicators).
	const datesWithEvents = new Set();
	events.forEach( ( event ) => {
		const key = event.date_utc || event.date || '';
		if ( key ) {
			datesWithEvents.add( key );
		}
	} );

	// Mobile: render mini-calendar + agenda instead of the full grid.
	if ( isMobile ) {
		return (
			<div className="nb-calendar-month nb-calendar-month--mobile">
				<MiniCalendar
					anchor={ anchor }
					firstDay={ firstDay }
					dateFrom={ dateFrom }
					datesWithEvents={ datesWithEvents }
					selectedDate={ mobileSelectedDate }
					onDateSelect={ setMobileSelectedDate }
				/>
				<MobileAgenda
					events={ events }
					dateFrom={ gridStartStr }
					dateTo={ gridEndStr }
					selectedDate={ mobileSelectedDate }
					onDayClick={ onDayClick }
				/>
			</div>
		);
	}

	return (
		<div className="nb-calendar-month">
			{ /* Weekday header row */ }
			<div className="nb-calendar-weekday-header">
				{ dayLabels.map( ( label ) => (
					<div key={ label } className="nb-calendar-weekday-label">
						{ label }
					</div>
				) ) }
			</div>

			{ /* Week rows */ }
			{ weekRows.map( ( weekCells, weekIdx ) => {
				const weekDates = weekCells.map( toISO );
				const spans = buildSpansForWeek( multiDayEvents, weekDates );

				return (
					<div key={ weekIdx } className="nb-calendar-week-row">
						{ /* Multi-day spanning events layer */ }
						{ spans.length > 0 && (
							<div className="nb-calendar-spanning-layer">
								{ spans.map( ( span ) => {
									const spanClasses = [
										'nb-calendar-spanning-event',
										span.event.is_past
											? 'nb-calendar-spanning-event--past'
											: '',
										span.isStart
											? 'nb-calendar-spanning-event--start'
											: '',
										span.isEnd
											? 'nb-calendar-spanning-event--end'
											: '',
									]
										.filter( Boolean )
										.join( ' ' );

									return (
										<EventCell
											key={ `${ span.event.id }-${ weekIdx }` }
											event={ span.event }
											isAllDay
											useLocalTz={ useLocalTz }
											spanStyle={ {
												gridColumn: `${ span.colStart } / ${ span.colEnd }`,
											} }
											spanClassName={ spanClasses }
										/>
									);
								} ) }
							</div>
						) }

						{ /* Day cells */ }
						<div className="nb-calendar-week-cells">
							{ weekCells.map( ( date ) => {
								const dateStr = toISO( date );
								const isToday = dateStr === todayStr;
								const isOutside =
									! dateStr.startsWith( monthYear );
								const dayEvents = singleByDate[ dateStr ] || [];
								const isActive = activeDate === dateStr;

								let cellClass = 'nb-calendar-day-cell';
								if ( isOutside ) {
									cellClass +=
										' nb-calendar-day-cell--outside';
								}
								if ( isToday ) {
									cellClass += ' nb-calendar-day-cell--today';
								}
								if ( dayEvents.length > 0 ) {
									cellClass +=
										' nb-calendar-day-cell--has-events';
								}

								return (
									<div
										key={ dateStr }
										className={ cellClass }
										onClick={ () => {
											if ( dayEvents.length > 0 ) {
												setActiveDate(
													isActive ? null : dateStr
												);
											}
										} }
									>
										<button
											className={ `nb-calendar-day-number${
												isOutside
													? ' nb-calendar-day-number--outside'
													: ''
											}` }
											onClick={ ( e ) => {
												e.stopPropagation();
												onDayClick( date );
											} }
											aria-label={ dateStr }
										>
											{ date.getDate() }
										</button>

										{ /* Colored event dot badges */ }
										{ dayEvents.length > 0 && (
											<div className="nb-calendar-day-dots">
												{ dayEvents.slice( 0, eventsPerCell ).map(
													( event, idx ) => {
														const color =
															getDotColor(
																event,
																idx
															);
														return (
															<span
																key={ event.id }
																className={ `nb-calendar-dot${
																	event.is_past
																		? ' nb-calendar-dot--past'
																		: ''
																}` }
																style={ {
																	backgroundColor:
																		color.bg,
																	color: color.text,
																	borderColor:
																		color.border,
																} }
																title={
																	event.title
																}
															>
																{ String(
																	idx + 1
																).padStart(
																	2,
																	'0'
																) }
															</span>
														);
													}
												) }
												{ dayEvents.length > eventsPerCell && (
													<span
														className="nb-calendar-dot nb-calendar-dot--more"
														title={ strings.moreEvents?.replace(
															'%d',
															dayEvents.length - eventsPerCell
														) || `+${ dayEvents.length - eventsPerCell } more` }
													>
														+{ dayEvents.length - eventsPerCell }
													</span>
												) }
											</div>
										) }

										{ /* Day popover — shows full event list on click */ }
										{ isActive && (
											<DayPopover
												dateStr={ dateStr }
												events={ dayEvents }
												useLocalTz={ useLocalTz }
												onClose={ () =>
													setActiveDate( null )
												}
												onDayClick={ onDayClick }
											/>
										) }
									</div>
								);
							} ) }
						</div>
					</div>
				);
			} ) }

			{ events.length === 0 && (
				<p className="nb-calendar-error">
					{ strings.noEvents || 'No events this period.' }
				</p>
			) }
		</div>
	);
}
